"use client";

import React, { useState, useEffect, useCallback } from "react";

interface HeadingItem {
  id: string;
  text: string;
  level: 2 | 3;
}

interface MobileTableOfContentsProps {
  content: string;
}

export function MobileTableOfContents({ content }: MobileTableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [headings, setHeadings] = useState<HeadingItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const allHeadings: HeadingItem[] = [];
    let match: RegExpExecArray | null;

    const h2Regex = /<h2[^>]*id="([^"]+)"[^>]*>(.*?)<\/h2>/g;
    while ((match = h2Regex.exec(content)) !== null) {
      const text = match[2].replace(/<[^>]*>/g, "");
      allHeadings.push({ id: match[1], text, level: 2 });
    }

    const h3Regex = /<h3[^>]*id="([^"]+)"[^>]*>(.*?)<\/h3>/g;
    while ((match = h3Regex.exec(content)) !== null) {
      const text = match[2].replace(/<[^>]*>/g, "");
      allHeadings.push({ id: match[1], text, level: 3 });
    }

    allHeadings.sort((a, b) => {
      const idxA = content.indexOf(`id="${a.id}"`);
      const idxB = content.indexOf(`id="${b.id}"`);
      return idxA - idxB;
    });

    Promise.resolve().then(() => setHeadings(allHeadings));

    if (allHeadings.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -60% 0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    allHeadings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });

    return () => {
      allHeadings.forEach((h) => {
        const el = document.getElementById(h.id);
        if (el) observer.unobserve(el);
      });
    };
  }, [content]);

  const handleScrollTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
      setActiveId(id);
      setIsOpen(false);
    }
  }, []);

  const activeHeading = headings.find((h) => h.id === activeId);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (headings.length === 0) return null;

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-[var(--accent)] text-white rounded-full shadow-lg p-4 flex items-center gap-2 hover:bg-[var(--accent-dark)] transition-transform duration-150 scale-100 active:scale-95 cursor-pointer"
        aria-label="Open table of contents"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="8" y1="6" x2="21" y2="6"></line>
          <line x1="8" y1="12" x2="21" y2="12"></line>
          <line x1="8" y1="18" x2="21" y2="18"></line>
          <line x1="3" y1="6" x2="3.01" y2="6"></line>
          <line x1="3" y1="12" x2="3.01" y2="12"></line>
          <line x1="3" y1="18" x2="3.01" y2="18"></line>
        </svg>
        {activeHeading && (
          <span className="text-[10px] font-mono font-bold uppercase tracking-wider max-w-[100px] truncate pr-1 hidden sm:block">
            {activeHeading.text}
          </span>
        )}
      </button>

      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Slide-up drawer */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-xl shadow-2xl border-t border-[var(--border)] transition-transform duration-200 ease-out max-h-[65vh] ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[var(--line)]">
          <h3 className="font-heading-condensed font-extrabold text-base text-[var(--ink)] flex items-center gap-2 uppercase">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--accent)]">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
            </svg>
            On This Page
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-lg hover:bg-[var(--surface-muted)] transition-colors cursor-pointer"
            aria-label="Close table of contents"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--ink-soft)]">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <nav className="overflow-y-auto px-6 py-4 flex flex-col gap-2 max-h-[calc(65vh-60px)] font-mono text-[11px] uppercase tracking-wider text-left">
          {headings.map((heading) => {
            const isActive = activeId === heading.id;
            const isH2 = heading.level === 2;
            return (
              <button
                key={heading.id}
                onClick={() => handleScrollTo(heading.id)}
                className={`text-left py-2.5 border-l-2 pl-4 transition-all duration-150 cursor-pointer ${
                  isActive
                    ? "border-[var(--accent)] text-[var(--accent)] font-bold"
                    : "border-[var(--line)] text-[var(--ink-soft)]"
                } ${isH2 ? "text-[11px]" : "text-[10px] ml-4"}`}
              >
                {heading.text}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}
